import React, { useState, useEffect } from "react";
import { getDatabase, ref, push, set, get, onValue } from "firebase/database";
import { getAuth } from "firebase/auth";
import {
  Gift,
  CheckCircle,
  X,
  AlertTriangle,
  Clock,
  User,
  Mail,
  Calendar,
  MessageSquare,
  Badge,
} from "lucide-react";

const   FreeTrialRequest = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [error, setError] = useState(null);
  const [isUserVip, setIsUserVip] = useState(false);
  const [requestDate, setRequestDate] = useState(null);

  useEffect(() => {
    // Intentar obtener datos del usuario actual
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      setEmail(user.email || "");

      // Si hay un usuario autenticado, buscar su información en la base de datos
      const db = getDatabase();
      const userRef = ref(db, `usuarios/${user.uid}`);
      get(userRef).then((snapshot) => {
        if (snapshot.exists()) {
          const userData = snapshot.val();
          setName(userData.name || "");

          // Verificar si el usuario es VIP
          if (userData.typeU === "Cuenta Vip") {
            setIsUserVip(true);
          }
        }
      });

      // Verificar si el usuario ya ha solicitado una prueba
      const userTrialsRef = ref(db, "/App/prueba-users");
      get(userTrialsRef).then((snapshot) => {
        if (snapshot.exists()) {
          const trials = snapshot.val();
          const userTrials = Object.entries(trials).filter(
            ([_, trialData]) => trialData.email === user.email
          );

          if (userTrials.length > 0) {
            const [trialId, trialData] = userTrials[0];
            setRequestId(trialId);
            setSubmitted(true);
            setRequestStatus(trialData.status || "pendiente");
            setRequestDate(trialData.fechaSolicitud || null);
            if (trialData.comment) {
              setComment(trialData.comment);
            }
          }
        }
      });
    }
  }, []);

  // Escuchar cambios en el estado de la solicitud si ya se ha enviado
  useEffect(() => {
    if (requestId) {
      const db = getDatabase();
      const requestRef = ref(db, `/App/prueba-users/${requestId}`);

      const unsubscribe = onValue(requestRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setRequestStatus(data.status || "pendiente");
          setRequestDate(data.fechaSolicitud || null);
        }
      });

      return () => unsubscribe();
    }
  }, [requestId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !email.trim()) {
      setError("Por favor completa los campos obligatorios");
      return;
    }

    setIsSubmitting(true);

    try {
      const db = getDatabase();
      const trialRequestsRef = ref(db, "/App/prueba-users");

      // Crear un nuevo registro con un ID único
      const newRequestRef = push(trialRequestsRef);
      const now = new Date().toISOString();

      await set(newRequestRef, {
        name: name.trim(),
        email: email.trim(),
        comment: comment.trim() || "",
        status: "pendiente",
        fechaSolicitud: now,
        fechaActualizacion: now,
      });

      setRequestId(newRequestRef.key);
      setSubmitted(true);
      setRequestStatus("pendiente");
      setRequestDate(now);
      setIsOpen(false); // Cerrar el modal después de enviar
    } catch (error) {
      console.error("Error al enviar la solicitud:", error);
      setError(
        "Ocurrió un error al enviar tu solicitud. Por favor intenta de nuevo."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Formato de fecha legible
  const formatDate = (isoDate) => {
    if (!isoDate) return "N/A";

    const date = new Date(isoDate);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Obtener el color según el estado
  const getStatusColor = (status) => {
    switch (status) {
      case "pendiente":
        return "bg-yellow-500";
      case "aprobado":
        return "bg-green-500";
      case "rechazado":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  // Obtener el texto según el estado
  const getStatusText = (status) => {
    switch (status) {
      case "pendiente":
        return "En revisión";
      case "aprobado":
        return "Aprobado";
      case "rechazado":
        return "Rechazado";
      default:
        return "Desconocido";
    }
  };

  // Renderiza la tarjeta de certificación
  const renderCertificateCard = () => {
    return (
      <div className="border border-purple-500 rounded-lg overflow-hidden bg-gradient-to-r from-gray-900 to-gray-800 shadow-lg m-3 mb-6">
        <div className="bg-purple-900 p-3 flex items-center justify-between">
          <div className="flex items-center">
            <Badge className="text-purple-300 mr-2" size={20} />
            <h3 className="text-lg font-bold text-white">
              Certificado de Solicitud
            </h3>
          </div>
          <div
            className={`${getStatusColor(
              requestStatus
            )} text-xs font-bold text-white px-3 py-1 rounded-full`}
          >
            {getStatusText(requestStatus)}
          </div>
        </div>

        <div className="p-4">
          <div className="space-y-3">
            <div className="flex items-start">
              <User className="text-purple-400 mr-3 mt-1" size={18} />
              <div>
                <p className="text-xs text-purple-300 mb-1">Solicitante</p>
                <p className="text-white font-medium">{name}</p>
              </div>
            </div>

            <div className="flex items-start">
              <Mail className="text-purple-400 mr-3 mt-1" size={18} />
              <div>
                <p className="text-xs text-purple-300 mb-1">
                  Correo electrónico
                </p>
                <p className="text-white">{email}</p>
              </div>
            </div>

            <div className="flex items-start">
              <Calendar className="text-purple-400 mr-3 mt-1" size={18} />
              <div>
                <p className="text-xs text-purple-300 mb-1">
                  Fecha de solicitud
                </p>
                <p className="text-white">{formatDate(requestDate)}</p>
              </div>
            </div>

            {comment && (
              <div className="flex items-start">
                <MessageSquare
                  className="text-purple-400 mr-3 mt-1"
                  size={18}
                />
                <div>
                  <p className="text-xs text-purple-300 mb-1">Comentario</p>
                  <p className="text-white">{comment}</p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 border-t border-gray-700 pt-4">
            <div className="flex justify-center">
              <div className="text-center">
                <p className="text-xs text-gray-400">ID de solicitud</p>
                <p className="text-xs font-mono text-gray-300 mt-1">
                  {requestId}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStatusMessage = () => {
    return (
      <div className="space-y-6">
        {renderCertificateCard()}

        <div className="bg-gray-800 p-4 rounded-lg">
          {requestStatus === "pendiente" && (
            <div className="flex flex-col items-center text-center">
              <div className="bg-yellow-500 p-3 rounded-full mb-3">
                <Clock className="text-gray-900" size={24} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Solicitud en revisión
              </h3>
              <p className="text-gray-300">
                Estamos evaluando tu solicitud de prueba gratuita. Te
                notificaremos cuando tengamos una respuesta.
              </p>
            </div>
          )}

          {requestStatus === "aprobado" && (
            <div className="flex flex-col items-center text-center">
              <div className="bg-green-500 p-3 rounded-full mb-3">
                <CheckCircle className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                ¡Felicidades! Tu solicitud fue aprobada
              </h3>
              <p className="text-gray-300">
                Ya tienes acceso a todas las funciones VIP. Disfruta de tu
                período de prueba gratuita.
              </p>
            </div>
          )}

          {requestStatus === "rechazado" && (
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-500 p-3 rounded-full mb-3">
                <X className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Solicitud no aprobada
              </h3>
              <p className="text-gray-300">
                Lo sentimos, tu solicitud de prueba gratuita no pudo ser
                aprobada en este momento. Considera adquirir nuestro plan VIP
                para acceder a todas las funciones premium.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-4 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-all duration-200"
              >
                Intentar de nuevo
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Si el usuario ya es VIP, mostrar mensaje informativo
  if (isUserVip) {
    return (
      <div className="w-full max-w-md mx-auto bg-gradient-to-r from-green-900 to-green-800 p-4 rounded-lg border border-green-500">
        <div className="flex items-center text-white">
          <CheckCircle className="text-green-400 mr-3" size={24} />
          <div>
            <h3 className="font-bold">¡Ya eres miembro VIP!</h3>
            <p className="text-sm text-green-200 mt-1">
              Ya estás disfrutando de todos los beneficios premium de nuestra
              plataforma.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Si el usuario ya tiene una solicitud, mostrar el certificado directamente
  if (submitted) {
    return (
      <div className="w-full max-w-md mx-auto">{renderStatusMessage()}</div>
    );
  }

  // Mostrar mensaje si no hay solicitudes
  if (!submitted && !isUserVip) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 mb-4">
          <div className="flex items-center justify-center text-gray-300 mb-3">
            <AlertTriangle className="text-yellow-500 mr-2" size={20} />
            <p>No tienes ninguna solicitud de prueba gratuita activa.</p>
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium px-4 py-3 rounded-lg transition-all duration-200 flex items-center justify-center"
          >
            <Gift className="mr-2" size={20} />
            Solicitar prueba gratuita VIP
          </button>
        </div>

        {isOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50 p-4">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 sm:p-6 rounded-xl shadow-xl border border-purple-500 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <Gift className="text-purple-500 mr-2" size={24} />
                  <h2 className="text-xl font-bold text-white">
                    Prueba Gratuita VIP
                  </h2>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-gray-800 p-4 rounded-lg mb-4">
                  <p className="text-gray-300">
                    Experimenta todas las ventajas de ser miembro VIP con
                    nuestra prueba gratuita. Completa el formulario para
                    solicitar acceso.
                  </p>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1">
                    Nombre completo <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Tu nombre"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1">
                    Correo electrónico <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="tucorreo@ejemplo.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1">
                    Comentario <span className="text-gray-400">(opcional)</span>
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[100px] resize-y"
                    placeholder="¿Por qué estás interesado en la prueba VIP? (opcional)"
                  />
                </div>

                {error && (
                  <div className="bg-red-900 bg-opacity-50 border border-red-500 text-red-200 p-3 rounded-lg text-sm">
                    <div className="flex items-start">
                      <AlertTriangle
                        className="text-red-400 mr-2 mt-0.5"
                        size={16}
                      />
                      <p>{error}</p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg mr-2"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`bg-purple-600 hover:bg-purple-700 text-white font-medium px-6 py-2 rounded-lg transition-all duration-200 flex items-center ${
                      isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                        Enviando...
                      </>
                    ) : (
                      "Solicitar"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default FreeTrialRequest;
